import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Divider,
  LinearProgress,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import DataTable, { Column } from '../../components/common/DataTable';
import StatusChip from '../../components/common/StatusChip';
import { getDeepfakeAnalyses } from '../../services/api/securityApi';
import { DeepfakeAnalysis, DeepfakeFilters } from '../../types/analysis';
import { formatDateTime, formatFileSize, formatPercentage } from '../../utils/formatters';

const fileTypeLabels: Record<string, string> = {
  video: '비디오',
  audio: '오디오',
};

const DeepfakeAnalysisPage: React.FC = () => {
  const [filters, setFilters] = useState<DeepfakeFilters>({
    page: 0,
    limit: 20,
  });
  const [selectedAnalysis, setSelectedAnalysis] = useState<DeepfakeAnalysis | null>(null);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);

  const { data, isLoading } = useQuery({
    queryKey: ['deepfakeAnalyses', filters],
    queryFn: () => getDeepfakeAnalyses(filters),
  });

  const handlePageChange = (page: number) => {
    setFilters(prev => ({ ...prev, page }));
  };

  const handleRowsPerPageChange = (limit: number) => {
    setFilters(prev => ({ ...prev, limit, page: 0 }));
  };

  const handleFileTypeFilterChange = (value: string) => {
    setFilters(prev => ({
      ...prev,
      fileType: value === 'all' ? undefined : (value as 'video' | 'audio'),
      page: 0,
    }));
  };

  const handleRowClick = (analysis: DeepfakeAnalysis) => {
    setSelectedAnalysis(analysis);
    setDetailDialogOpen(true);
  };

  const handleCloseDetailDialog = () => {
    setDetailDialogOpen(false);
    setSelectedAnalysis(null);
  };

  const getProbabilityColor = (probability: number): 'success' | 'warning' | 'error' => {
    if (probability < 0.3) return 'success';
    if (probability < 0.7) return 'warning';
    return 'error';
  };

  const columns: Column<DeepfakeAnalysis>[] = [
    {
      id: 'fileName',
      label: '파일명',
      minWidth: 200,
    },
    {
      id: 'fileType',
      label: '파일 유형',
      minWidth: 100,
      format: (value: string) => fileTypeLabels[value] || value,
    },
    {
      id: 'fileSize',
      label: '파일 크기',
      minWidth: 120,
      format: (value: number) => formatFileSize(value),
    },
    {
      id: 'result',
      label: '딥페이크 확률',
      minWidth: 150,
      align: 'center',
      format: (_value: any, row: DeepfakeAnalysis) => {
        const probability = row.result.deepfakeProbability;
        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <LinearProgress
              variant="determinate"
              value={probability * 100}
              color={getProbabilityColor(probability)}
              sx={{ flex: 1, height: 8, borderRadius: 4 }}
            />
            <Typography variant="body2">{formatPercentage(probability)}</Typography>
          </Box>
        );
      },
    },
    {
      id: 'confidence',
      label: '신뢰도',
      minWidth: 100,
      align: 'center',
      format: (_value: any, row: DeepfakeAnalysis) =>
        formatPercentage(row.result.confidence),
    },
    {
      id: 'createdAt',
      label: '분석일',
      minWidth: 150,
      format: (value: Date) => formatDateTime(value),
      sortable: true,
    },
  ];

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        딥페이크 분석 관리
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <FormControl sx={{ minWidth: 200 }}>
            <InputLabel>파일 유형 필터</InputLabel>
            <Select
              value={filters.fileType || 'all'}
              label="파일 유형 필터"
              onChange={e => handleFileTypeFilterChange(e.target.value)}
              size="small"
            >
              <MenuItem value="all">전체</MenuItem>
              <MenuItem value="video">비디오</MenuItem>
              <MenuItem value="audio">오디오</MenuItem>
            </Select>
          </FormControl>
        </Stack>
      </Paper>

      <DataTable
        columns={columns}
        data={(data?.items || []).map(item => ({ ...item, id: item._id }))}
        total={data?.total || 0}
        page={filters.page}
        rowsPerPage={filters.limit}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onRowClick={(row: any) => handleRowClick(row as DeepfakeAnalysis)}
        loading={isLoading}
      />

      {/* 상세 정보 모달 */}
      <Dialog open={detailDialogOpen} onClose={handleCloseDetailDialog} maxWidth="md" fullWidth>
        <DialogTitle>딥페이크 분석 상세 정보</DialogTitle>
        <DialogContent>
          {selectedAnalysis && (
            <Box sx={{ pt: 2 }}>
              <Stack spacing={2}>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      파일명
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {selectedAnalysis.fileName}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      파일 유형
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {fileTypeLabels[selectedAnalysis.fileType] || selectedAnalysis.fileType}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      파일 크기
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatFileSize(selectedAnalysis.fileSize)}
                    </Typography>
                  </Box>
                  <Box sx={{ flex: '1 1 45%', minWidth: 200 }}>
                    <Typography variant="subtitle2" color="text.secondary">
                      분석일
                    </Typography>
                    <Typography variant="body1" gutterBottom>
                      {formatDateTime(selectedAnalysis.createdAt)}
                    </Typography>
                  </Box>
                </Box>
                <Divider />
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    딥페이크 확률
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={selectedAnalysis.result.deepfakeProbability * 100}
                      color={getProbabilityColor(selectedAnalysis.result.deepfakeProbability)}
                      sx={{ flex: 1, height: 12, borderRadius: 6 }}
                    />
                    <Typography variant="h6">
                      {formatPercentage(selectedAnalysis.result.deepfakeProbability)}
                    </Typography>
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    신뢰도
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <LinearProgress
                      variant="determinate"
                      value={selectedAnalysis.result.confidence * 100}
                      sx={{ flex: 1, height: 12, borderRadius: 6 }}
                    />
                    <Typography variant="h6">
                      {formatPercentage(selectedAnalysis.result.confidence)}
                    </Typography>
                  </Box>
                </Box>
                {selectedAnalysis.fileUrl && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        파일 URL
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}
                      >
                        {selectedAnalysis.fileUrl}
                      </Typography>
                    </Box>
                  </>
                )}
                {selectedAnalysis.result.metadata && (
                  <>
                    <Divider />
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                        메타데이터
                      </Typography>
                      <Paper
                        sx={{
                          p: 2,
                          bgcolor: 'grey.100',
                          maxHeight: 300,
                          overflow: 'auto',
                        }}
                      >
                        <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                          {JSON.stringify(selectedAnalysis.result.metadata, null, 2)}
                        </pre>
                      </Paper>
                    </Box>
                  </>
                )}
              </Stack>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDetailDialog}>닫기</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DeepfakeAnalysisPage;
